import * as R from 'ramda';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { getEnv } from '~/utils';
import { api } from '~/api';
import { authService } from '~/services/auth-service';
import { useStoreActions } from 'easy-peasy';
import LoadingSite from 'Components/LoadingSite';

export function PrivateRoute({ component: Component, user, ...rest }) {
  const checkAuth = useStoreActions((actions) => actions.auth.checkAuth);
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');
  const clientId = getEnv(import.meta.env.VITE_APP_WEB_CLIENT_ID);
  const urlSolution = urlParams.get('solution_id');

  async function tokenAuth() {
    try {
      const response = await api.authenticate(token, clientId);
      const { token_type, user_id, access_token, expires_in } = response;
      checkAuth({
        token: {
          user_id,
          access_token,
          token_type,
          expires_in,
        },
        target: R.pathOr({}, ['target'], response),
        redirect: false,
      });
      history.replace({
        pathname: location.pathname,
        search: null,
      });
    } catch {
      const params = new URLSearchParams(location.search);
      params.delete('token');
      const search = params.toString();
      history.replace('/signin', {
        from: `${location.pathname}${search ? `?${search}` : ''}`,
      });
    }
  }

  useEffect(() => {
    if (!user) {
      // Don't attempt auth if we're already going to signin or coming from signout
      if (location.pathname === '/signin' || location.hash === '#signout') {
        return;
      }

      if (token) {
        tokenAuth();
      } else {
        const storedToken = authService.loadToken();
        if (storedToken) {
          // only try to refresh if we have a stored token
          // redirect to /home only when navigating to root (not on refresh)
          const navEntry = performance.getEntriesByType(
            'navigation'
          )[0] as PerformanceNavigationTiming;
          const isPageReload = navEntry?.type === 'reload';
          checkAuth({
            redirect: location.pathname === '/' && !isPageReload,
            camefrom: location.pathname,
            urlSolution,
          });
        } else {
          // no token at all, redirect to signin
          history.replace('/signin', {
            from: location.pathname + location.search,
          });
        }
      }
    }
  }, [user]);

  return (
    <Route
      {...rest}
      render={(props) => (user ? <Component {...props} /> : <LoadingSite />)}
    />
  );
}
